import { Component } from '@theme/component';

/**
 * Parallax hero
 *
 * The section is a normal-flow box with `clip-path: inset(0)`. In parallax mode its
 * sky / photo / snow / copy layers are `position: fixed` and sized to the box, so they
 * stay put while the box (and therefore the clip window) scrolls away and the next
 * section rises over them. Nothing outside the section is touched.
 *
 * This element only has to:
 *  - measure where the box sits (top / left / width) so the fixed layers line up with it,
 *  - measure how far the header overlaps the box (transparent header) so the copy clears it,
 *  - drive the gentle scroll drift + fade of the copy and photo,
 *  - run the snowfall canvas while the hero is on screen.
 */

/** Fraction of the scrolled distance the copy and photo drift upward. */
const DRIFT = 0.12;
/** The copy is fully faded once this fraction of the viewport height has been scrolled. */
const FADE_DISTANCE = 0.42;
const MAX_DPR = 2;
/** Flakes per square CSS pixel at intensity 5. */
const FLAKE_DENSITY = 1 / 9000;
const DEFAULT_INTENSITY = 5;
/** Default "roof visible" share of the viewport height, as a percentage. */
const DEFAULT_PEEK = 30;
/** Never shrink the roof peek below this share of the box height. */
const MIN_PEEK_RATIO = 0.12;
/** Minimum breathing room between the copy and the roofline. */
const ROOF_GAP = 24;

const reducedMotionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

/**
 * @typedef {object} Flake
 * @property {number} x
 * @property {number} y
 * @property {number} r
 * @property {number} vy
 * @property {number} sway
 * @property {number} swaySpeed
 * @property {number} phase
 * @property {number} alpha
 */

/**
 * @typedef {object} ParallaxHeroRefs
 * @property {HTMLElement} [content]
 * @property {HTMLElement} [copy]
 * @property {HTMLElement} [photo]
 * @property {HTMLCanvasElement} [canvas]
 *
 * @extends {Component<ParallaxHeroRefs>}
 */
class CustomParallaxHero extends Component {
  /** @type {AbortController | null} */
  #abort = null;
  /** @type {IntersectionObserver | null} */
  #intersection = null;
  /** @type {ResizeObserver | null} */
  #resize = null;
  /** @type {CanvasRenderingContext2D | null} */
  #ctx = null;
  /** @type {Flake[]} */
  #flakes = [];

  /** Document-space top of the box (what the fixed layers are pinned to). */
  #top = 0;
  #width = 0;
  #height = 0;
  #measuredAtTop = false;
  #inView = true;
  #scrollFrame = 0;
  #snowFrame = 0;
  #lastTick = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#abort = new AbortController();
    const { signal } = this.#abort;

    this.#applyMotionPreference();
    this.#measure();

    window.addEventListener('resize', this.#onResize, { signal });
    // Horizon scrolls `.page-wrapper` on desktop and the window on smaller screens.
    window.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    document.querySelector('.page-wrapper')?.addEventListener('scroll', this.#onScroll, { passive: true, signal });
    document.addEventListener('visibilitychange', this.#syncSnow, { signal });
    this.addEventListener('wheel', this.#onWheel, { passive: false, signal });
    reducedMotionQuery.addEventListener('change', this.#onMotionChange, { signal });

    if (window.Shopify?.designMode) {
      // Sections added/removed/reordered above the hero move it; re-measure.
      for (const type of ['shopify:section:load', 'shopify:section:unload', 'shopify:section:reorder']) {
        document.addEventListener(type, this.#onResize, { signal });
      }
    }

    this.#intersection = new IntersectionObserver(
      (entries) => {
        this.#inView = entries.some((entry) => entry.isIntersecting);
        this.#syncSnow();
        if (this.#inView) this.#onScroll();
      },
      { rootMargin: '64px' }
    );
    this.#intersection.observe(this);

    this.#resize = new ResizeObserver(() => this.#measure());
    this.#resize.observe(this);
    // The header chrome above the hero decides where the box starts; watch it too so a
    // late font swap or announcement change cannot leave the pinned layers misaligned.
    for (const chrome of document.querySelectorAll('#header-group > *')) {
      this.#resize.observe(chrome);
    }
    document.fonts?.ready.then(() => this.isConnected && this.#measure());
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abort?.abort();
    this.#abort = null;
    this.#intersection?.disconnect();
    this.#intersection = null;
    this.#resize?.disconnect();
    this.#resize = null;
    cancelAnimationFrame(this.#scrollFrame);
    this.#scrollFrame = 0;
    cancelAnimationFrame(this.#snowFrame);
    this.#snowFrame = 0;
    this.#ctx = null;
  }

  get #parallaxEnabled() {
    return this.classList.contains('cph--parallax') && !this.classList.contains('cph--static');
  }

  get #snowEnabled() {
    return Boolean(this.refs.canvas) && !this.classList.contains('cph--static');
  }

  /**
   * Horizon turns `.page-wrapper` into the scroller at >= 990px (`base.css`) and hides the
   * document's own overflow. Returns it only while it is actually the scrolling element.
   * @returns {HTMLElement | null}
   */
  #wrapperScroller() {
    const wrapper = document.querySelector('.page-wrapper');
    if (
      wrapper instanceof HTMLElement &&
      wrapper.scrollHeight > wrapper.clientHeight &&
      /(auto|scroll)/.test(getComputedStyle(wrapper).overflowY)
    ) {
      return wrapper;
    }
    return null;
  }

  /** Current scroll offset of whichever element scrolls the page. */
  #scrollTop() {
    const wrapper = this.#wrapperScroller();
    if (wrapper) return wrapper.scrollTop;
    return window.scrollY || document.documentElement.scrollTop || 0;
  }

  /**
   * The pinned layers are `position: fixed`, so their scroll chain is the document — which
   * cannot scroll while `.page-wrapper` is the scroller. CSS keeps the layers out of hit-testing
   * for exactly this reason, but the CTAs inside them must stay clickable, so a wheel that lands
   * on one still has to be forwarded to the real scroller by hand.
   * @param {WheelEvent} event
   */
  #onWheel = (event) => {
    const scroller = this.#wrapperScroller();
    if (!scroller || event.defaultPrevented || event.ctrlKey) return;
    if (!(event.target instanceof Element) || !event.target.closest('.cph__layer')) return;

    // deltaMode: 0 = pixels, 1 = lines, 2 = pages.
    let scale = 1;
    if (event.deltaMode === 1) scale = 16;
    else if (event.deltaMode === 2) scale = scroller.clientHeight;

    // `.page-wrapper` sets `scroll-behavior: smooth`, and the wheel gesture itself aborts any
    // smooth scroll it would start — so the delta has to be applied instantly to stick.
    scroller.scrollBy({ top: event.deltaY * scale, left: event.deltaX * scale, behavior: 'instant' });
    event.preventDefault();
  };

  #applyMotionPreference() {
    const reduce = reducedMotionQuery.matches;
    this.classList.toggle('cph--static', reduce);
    if (reduce) {
      this.style.removeProperty('--cph-drift-y');
      this.style.removeProperty('--cph-copy-opacity');
      this.classList.remove('cph--copy-hidden');
    }
  }

  #onMotionChange = () => {
    this.#applyMotionPreference();
    this.#measure();
    this.#syncSnow();
  };

  #onResize = () => {
    this.#measuredAtTop = false;
    this.#measure();
  };

  /**
   * Pin the fixed layers to the box and record where the header ends.
   * Runs on connect, resize, and whenever the box changes size.
   */
  #measure = () => {
    const rect = this.getBoundingClientRect();
    if (!rect.width || !rect.height) return;

    const scrollTop = this.#scrollTop();
    this.#top = Math.round(rect.top + scrollTop);
    this.#width = Math.round(rect.width);
    this.#height = Math.round(rect.height);

    this.style.setProperty('--cph-top', `${this.#top}px`);
    this.style.setProperty('--cph-left', `${Math.round(rect.left)}px`);
    this.style.setProperty('--cph-width', `${this.#width}px`);

    // The header only overlaps the hero when it is transparent (absolute) on the home page.
    // Measure it at the top of the page, where nothing is stuck yet.
    if (scrollTop < 2) {
      const header = document.querySelector('header-component');
      const overlap = header ? Math.max(0, header.getBoundingClientRect().bottom - rect.top) : 0;
      this.style.setProperty('--cph-header-overlap', `${Math.round(overlap)}px`);
      this.#measuredAtTop = true;
    }

    this.#fitPhotoBelowCopy(rect);
    this.#resizeCanvas();
    this.#onScroll();
  };

  /**
   * On short viewports the copy can be taller than the sky above the roof. Rather than let
   * the buttons sit on the roof, lower the photo (shrink the peek) until it clears the copy.
   * @param {DOMRect} rect - the box
   */
  #fitPhotoBelowCopy(rect) {
    const { copy } = this.refs;
    if (!(copy instanceof HTMLElement)) return;

    const peekPercent = Number(this.dataset.peek) || DEFAULT_PEEK;
    const peekPx = (peekPercent / 100) * (window.innerHeight || rect.height);
    const drift = parseFloat(this.style.getPropertyValue('--cph-drift-y')) || 0;
    const copyBottom = copy.getBoundingClientRect().bottom - drift - rect.top;
    const available = rect.height - copyBottom - ROOF_GAP;
    const effective = Math.max(rect.height * MIN_PEEK_RATIO, Math.min(peekPx, available));

    if (effective < peekPx - 1) {
      this.style.setProperty('--cph-peek', `${Math.round(effective)}px`);
    } else {
      this.style.removeProperty('--cph-peek');
    }
  }

  #onScroll = () => {
    if (this.#scrollFrame) return;
    this.#scrollFrame = requestAnimationFrame(() => {
      this.#scrollFrame = 0;
      this.#updateScroll();
    });
  };

  #updateScroll() {
    const rect = this.getBoundingClientRect();
    const scrolled = Math.max(0, this.#top - rect.top);

    if (scrolled < 2 && !this.#measuredAtTop) {
      this.#measure();
      return;
    }

    if (!this.#parallaxEnabled) return;

    const viewport = window.innerHeight || rect.height;
    const drift = -(scrolled * DRIFT);
    // The fade tracks the copy's *drifted* distance, not the raw scroll, so it stays gentle:
    // the copy is still around two thirds opaque by the time the hero has scrolled away, and
    // only fully fades far below the fold. (This matches the design prototype, which divided
    // `y * drift` — dropping that factor made the copy vanish roughly eight times too fast.)
    const progress = Math.min(1, (scrolled * DRIFT) / (viewport * FADE_DISTANCE));
    const opacity = Math.max(0, 1 - progress * 1.15);

    // Past the bottom of the box the layers are clipped away entirely; keep the copy out of the
    // tab order there, since it never fades to nothing on its own any more.
    const offScreen = rect.bottom <= 0;
    this.classList.toggle('cph--copy-hidden', offScreen || opacity <= 0.02);
    if (offScreen) return;

    this.style.setProperty('--cph-drift-y', `${drift.toFixed(1)}px`);
    this.style.setProperty('--cph-copy-opacity', opacity.toFixed(3));
  }

  /* ------------------------------------------------------------------ snow */

  #resizeCanvas() {
    const { canvas } = this.refs;
    if (!(canvas instanceof HTMLCanvasElement)) return;

    const box = canvas.getBoundingClientRect();
    const width = Math.round(box.width);
    const height = Math.round(box.height);
    if (!width || !height) return;

    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    canvas.width = Math.round(width * dpr);
    canvas.height = Math.round(height * dpr);

    this.#ctx = canvas.getContext('2d');
    this.#ctx?.setTransform(dpr, 0, 0, dpr, 0, 0);

    const intensity = Number(this.dataset.snowIntensity) || DEFAULT_INTENSITY;
    const count = Math.round(width * height * FLAKE_DENSITY * (intensity / DEFAULT_INTENSITY));
    this.#flakes = Array.from({ length: count }, () => this.#makeFlake(width, height, true));

    this.#syncSnow();
  }

  /**
   * @param {number} width
   * @param {number} height
   * @param {boolean} spread - true to place the flake anywhere in the box, false to start above it
   * @returns {Flake}
   */
  #makeFlake(width, height, spread) {
    const r = 0.8 + Math.random() * 2.6;
    return {
      x: Math.random() * width,
      y: spread ? Math.random() * height : -10 - Math.random() * 60,
      r,
      vy: 18 + r * 16 + Math.random() * 14,
      sway: 8 + Math.random() * 26,
      swaySpeed: 0.4 + Math.random() * 0.8,
      phase: Math.random() * Math.PI * 2,
      alpha: 0.35 + Math.random() * 0.6,
    };
  }

  /** Start or stop the canvas loop depending on visibility, motion preference and settings. */
  #syncSnow = () => {
    const shouldRun =
      this.isConnected &&
      this.#snowEnabled &&
      this.#inView &&
      document.visibilityState === 'visible' &&
      Boolean(this.#ctx);

    if (shouldRun && !this.#snowFrame) {
      this.#lastTick = 0;
      this.#snowFrame = requestAnimationFrame(this.#tick);
    } else if (!shouldRun && this.#snowFrame) {
      cancelAnimationFrame(this.#snowFrame);
      this.#snowFrame = 0;
    }
  };

  /** @param {number} now */
  #tick = (now) => {
    this.#snowFrame = requestAnimationFrame(this.#tick);

    const ctx = this.#ctx;
    const { canvas } = this.refs;
    if (!ctx || !(canvas instanceof HTMLCanvasElement)) return;

    const dpr = Math.min(window.devicePixelRatio || 1, MAX_DPR);
    const width = canvas.width / dpr;
    const height = canvas.height / dpr;
    const dt = this.#lastTick ? Math.min(0.05, (now - this.#lastTick) / 1000) : 0;
    this.#lastTick = now;
    const t = now / 1000;

    ctx.clearRect(0, 0, width, height);
    ctx.fillStyle = '#ffffff';

    for (const flake of this.#flakes) {
      flake.y += flake.vy * dt;
      if (flake.y - flake.r > height) Object.assign(flake, this.#makeFlake(width, height, false));

      const x = flake.x + Math.sin(t * flake.swaySpeed + flake.phase) * flake.sway;
      ctx.globalAlpha = flake.alpha;
      ctx.beginPath();
      ctx.arc(x, flake.y, flake.r, 0, Math.PI * 2);
      ctx.fill();
    }

    ctx.globalAlpha = 1;
  };
}

if (!customElements.get('custom-parallax-hero')) {
  customElements.define('custom-parallax-hero', CustomParallaxHero);
}
